<template>
	<ShowError
		v-if="showError"
		:connection="isConnected"
		:status-code="status"
	></ShowError>
</template>

<script setup lang="ts">
import ShowError from '@/components/elements/Errors/ShowError.vue'
import endpointNames from '@/constants/endpointNames'
import errorMessages from 'constants/errorMessages.ts'
import { find } from 'lodash'
import {
	useAlertStore,
	useBackendStore,
	useToastStore,
	useUserStore,
} from '@/stores/system'
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

const interval = ref()
const retryAfter = ref()
const messageTranslated = ref()

const { isConnected, status, isAvailable } = storeToRefs(useBackendStore())
const { isError: requestFailed, failure: requestFailure } =
	storeToRefs(useAlertStore())

const { clearAlert } = useAlertStore()
const { isLoggedIn } = storeToRefs(useUserStore())

const showError = computed(() => {
	return (
		!isAvailable.value ||
		!isConnected.value ||
		status.value !== 200 ||
		(status.value === 403 && requestFailure?.data.code === 3001)
	)
})

watch(requestFailed, (value) => {
	if (value) {
		errorShow(requestFailure.value)
	}
})

onMounted(() => {
	clearAlert()
})

const router = useRouter()

const { logout } = useUserStore()
const { toastError } = useToastStore()

function errorShow(failure) {
	let showError = failure?.message !== 'canceled'
	let logoutUser = false
	let message = failure.message
	let header =
		failure.status || failure.error
			? `${failure.status}: ${failure.error ? failure.error : ''}`
			: ''
	let endpointTranslated
	if (!isAvailable.value) {
		return
	}
	switch (failure.status) {
		case 401:
			if (failure.endpoint.indexOf('oauth/token') === -1) {
				showError = false
				logoutUser = true
				break
			}
			messageTranslated.value = find(errorMessages, {
				error: failure.error,
			})
			break
		case 400:
		case 403:
			if (failure.error === 'no database' && isLoggedIn.value) {
				logoutUser = true
			}
			messageTranslated.value =
				failure.error === 'invalid_subscription'
					? find(errorMessages, {
							error: failure.error,
							code: failure.data.code,
						})
					: find(errorMessages, {
							error: failure.error,
						})
			break
		case 404:
			if (router.currentRoute.value.fullPath.indexOf(failure.endpoint) > -1) {
				router.replace({ name: 'error', params: { id: 404 } })
			}
			return
		case 422:
			messageTranslated.value = find(errorMessages, {
				error: failure.error,
			})
			break
		case 429:
			retryAfter.value = failure.headers['retry-after']
			interval.value = setInterval(() => {
				timer()
			}, 1000)
			messageTranslated.value = find(errorMessages, {
				error: 'retry-after',
			})
			break
		case 412:
			messageTranslated.value = {}
			if (typeof failure.endpoint === 'string') {
				endpointTranslated = find(endpointNames, {
					name: failure.endpoint,
				})
			}
			messageTranslated.value.header = `${failure.status}: Anweisung fehlgeschlagen`
			messageTranslated.value.message = {
				key: 'component.toast.error.elementNotDeleted',
				params: { entity: 'component.toast.error.element' },
			}
			if (typeof endpointTranslated !== 'undefined') {
				messageTranslated.value.message = {
					key: 'component.toast.error.elementNotDeleted',
					params: { entity: endpointTranslated.singular },
				}
			}
			break
		default:
			// Must clear: messageTranslated is a long-lived ref, so an earlier
			// 400/403/422/429/412 in the same session would otherwise make the
			// truthy branch below toast the PREVIOUS error's text for this one.
			messageTranslated.value = undefined
			message = failure.message
			break
	}

	if (logoutUser) {
		logout()
		return
	}

	if (messageTranslated.value && showError) {
		header = messageTranslated.value.header
		message = messageTranslated.value.message

		toastError({
			header,
			message,
			onClosed: () => {
				clearAlert()
			},
		})
		failure.handled = true
		return failure
	} else if (showError) {
		toastError({
			onClosed: () => {
				clearAlert()
			},
		})
		/*failure.handled = false
        throw failure*/
	}
}
function timer() {
	if (retryAfter.value > 0) {
		retryAfter.value -= 1
		// this.$eventBus.$emit('retryAfter', this.retryAfter) //TODO:: gibt kein nativen ersatz
	} else if (retryAfter.value === 0) {
		clearInterval(interval.value)
	}
}
</script>

<style scoped></style>
